import PropTypes from "prop-types";
import * as React from "react";
import {
  Button,
  Dialog,
  IconButton,
  TextInput,
  PencilIcon,
  SaveIcon,
} from "../../../themeV2";
import css from "./AdvancedSettings.module.css";
import { currencyFormatter } from "../../../util/formatCurrency";
import { updateInfluencer } from "./service";

export default function ServiceBumpSection({
  influencer,
  setIsEditingServiceFeeBump,
}) {
  const [value, setValue] = React.useState(
    (influencer.service_fee_bump || 0) / 100,
  );
  const [isEditing, setIsEditing] = React.useState(false);
  const [confirmModal, setConfirmModal] = React.useState(false);
  const [errorText, setErrorText] = React.useState("");

  const handleToggle = React.useCallback(() => {
    if (!isEditing) {
      setIsEditing(true);
      setIsEditingServiceFeeBump(true);
      setErrorText("");
      return;
    }

    const newValue = Math.round(Number(value) * 100);
    if (newValue === (influencer.service_fee_bump || 0)) {
      setIsEditing(false);
      setIsEditingServiceFeeBump(false);
      return;
    }

    setConfirmModal(true);
  }, [isEditing, value, influencer, setIsEditingServiceFeeBump]);

  const handleConfirm = React.useCallback(async () => {
    const newValue = Math.round(Number(value) * 100);
    try {
      await updateInfluencer(influencer.influencer_id, {
        service_fee_bump: newValue,
      });
      setErrorText("");
      setConfirmModal(false);
      setIsEditing(false);
      setIsEditingServiceFeeBump(false);
    } catch (err) {
      console.error(err);
      setConfirmModal(false);
      setErrorText(
        err.message || "Failed to update service fee. Please try again.",
      );
    }
  }, [value, influencer, setIsEditingServiceFeeBump]);

  const handleCancel = React.useCallback(() => {
    setConfirmModal(false);
    setIsEditing(false);
    setIsEditingServiceFeeBump(false);
  }, [setIsEditingServiceFeeBump]);

  return (
    <>
      <p className={css.caption}>
        Set an optional additional service fee for the shop.
      </p>

      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "var(--space-2)",
          marginBlockStart: "var(--space-4)",
        }}
      >
        <TextInput
          type="text"
          value={String(value)}
          onChange={(e) => setValue(e.target.value)}
          disabled={!isEditing}
          leading="$"
          style={{ maxWidth: "12ch" }}
        />
        <IconButton
          onClick={handleToggle}
          aria-label={isEditing ? "Save" : "Edit"}
        >
          {isEditing ? <SaveIcon /> : <PencilIcon />}
        </IconButton>
      </div>

      {errorText && <p className={css.errorText}>{errorText}</p>}

      <Dialog.Root
        alert
        open={confirmModal}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
      >
        <Dialog.Popup size="sm">
          <Dialog.Title>Confirm service fee</Dialog.Title>
          <Dialog.Description>
            Set service fee bump for {influencer.name} to{" "}
            {currencyFormatter.format(value)}?
          </Dialog.Description>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "var(--space-2)",
              marginBlockStart: "var(--space-4)",
            }}
          >
            <Button variant="outlined" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>Confirm</Button>
          </div>
        </Dialog.Popup>
      </Dialog.Root>
    </>
  );
}

ServiceBumpSection.propTypes = {
  influencer: PropTypes.object.isRequired,
  setIsEditingServiceFeeBump: PropTypes.func.isRequired,
};
